import React, { useState, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import CourseForm from './Form';
import SmartCard from '../../../shared/components/SmartCard';
import GraphQL from '../../../GraphQL';
import { showNotificationAutoDestroy } from '../../../shared/components/ShowNotification';
import {
  changeLoadingFalse,
  changeLoadingTrue,
} from '../../../redux/actions/loadingActions';
import {
  courseListById,
  courseStepRemoveById,
  saveCourseMutation,
} from '../../../shared/queries/Course';
import { activeClassCountByCourse } from '../../../shared/queries/SchoolClass';
import { employeeList } from '../../../shared/queries/Employee';
import { ACTIVE_TAB } from '../../../shared/utils/constants';

const StudentFormHandler = (props) => {
  const { t, match, history, dispatch } = props;

  const [dto, setDto] = useState({
    id: 0,
    minNote: 0,
    maxNote: 10,
    minFrequency: 75,
    steps: [],
    curriculum: [],
    objectives: [],
  });
  const [formOn, setFormOn] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [step, setStep] = useState(ACTIVE_TAB.ONE);
  const [activeClassesByCourse, setActiveClassesByCourse] = useState(0);

  const nextStep = (tabIndex) => {
    let updatedStep = parseInt(step, 10) + parseInt(ACTIVE_TAB.ONE, 10);
    if (tabIndex) {
      updatedStep = parseInt(tabIndex, 10);
    }
    setStep(String(updatedStep));
  };

  const turnBackStep = () => {
    const updatedStep = parseInt(step, 10) - parseInt(ACTIVE_TAB.ONE, 10);
    setStep(String(updatedStep));
  };

  const showLoading = () => {
    dispatch(changeLoadingTrue());
  };

  const hideLoading = () => {
    dispatch(changeLoadingFalse());
  };

  const backClickHandler = () => {
    history.push('/minime/curso');
  };

  const loadActiveClassesByCourseId = (courseId) => {
    const query = activeClassCountByCourse(courseId);
    GraphQL(query, {})
      .then((res) => {
        setActiveClassesByCourse(res.activeClassCountByCourse);
      })
      .catch((error) => {
        console.error(error);
        setActiveClassesByCourse(0);
      });
  };

  const loadAllEmployees = () => {
    showLoading();
    const query = employeeList();
    GraphQL(query, {})
      .then((res) => {
        hideLoading();
        if (res.employeeListByFilter) {
          res.employeeListByFilter = res.employeeListByFilter.map((item) => ({
            ...item,
            label: item.name,
            value: item.id,
          }));
          setEmployees(res.employeeListByFilter);
        } else {
          showNotificationAutoDestroy(
            'danger',
            t('notifications.error'),
            t('notifications.record-not-found')
          );
          backClickHandler();
        }
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      });
  };

  const findById = (id, isNextStep) => {
    showLoading();
    const query = courseListById({ id });
    GraphQL(query, {})
      .then((res) => {
        hideLoading();
        if (res.courseListByFilter) {
          setDto({
            ...res.courseListByFilter[0],
            coordinator: {
              ...res.courseListByFilter[0].coordinator,
              value: res.courseListByFilter[0].coordinator.id,
              label: t(`${res.courseListByFilter[0].coordinator.name}`),
            },
            director: res.courseListByFilter[0].director
              ? {
                  ...res.courseListByFilter[0].director,
                  value: res.courseListByFilter[0].director.id,
                  label: t(`${res.courseListByFilter[0].director.name}`),
                }
              : null,
          });
          if (res.courseListByFilter[0].id !== 0 && isNextStep) {
            history.push(`/minime/curso/mnt/${res.courseListByFilter[0].id}`);
            nextStep();
          }
        } else {
          showNotificationAutoDestroy(
            'danger',
            t('notifications.error'),
            t('notifications.record-not-found')
          );
          backClickHandler();
        }
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('errors.title'),
          t('errors.error-to-loading')
        );
      });
  };

  const handleSubmit = (values) => {
    showLoading();
    const mutation = saveCourseMutation(values);
    GraphQL(mutation, {})
      .then((res) => {
        hideLoading();
        if (step === ACTIVE_TAB.THREE) {
          backClickHandler();
        } else {
          findById(res.courseSave.id, true);
        }
        showNotificationAutoDestroy(
          'success',
          t('success.title'),
          t('success.successfully-saved')
        );
      })
      .catch((error) => {
        console.error(error);
        hideLoading();
      });
  };

  const removeCourseStepById = (courseStep) => {
    showLoading();
    const mutation = courseStepRemoveById(courseStep.id);
    findById(dto.id, false);
    GraphQL(mutation, {})
      .then(() => {
        hideLoading();
        findById(dto.id, false);
        showNotificationAutoDestroy(
          'success',
          t('success.title'),
          t('success.successfully-saved')
        );
      })
      .catch((error) => {
        console.error(error);
        hideLoading();
      });
  };

  useEffect(() => {
    if (match.params.id && match.params.id > 0) {
      if (dto.id !== parseInt(match.params.id, 0)) {
        findById(match.params.id);
        loadActiveClassesByCourseId(match.params.id);
      } else {
        setFormOn(true);
      }
    } else {
      setFormOn(true);
    }
  }, [dto]);

  useEffect(() => {
    loadAllEmployees();
  }, []);

  return (
    <SmartCard title={t('panels.curso')}>
      {formOn && (
        <CourseForm
          saveForm={handleSubmit}
          backHandler={backClickHandler}
          employees={employees}
          dto={dto}
          dispatch={dispatch}
          step={step}
          turnBackStep={turnBackStep}
          nextStep={nextStep}
          removeCourseStep={removeCourseStepById}
          activeClassesByCourse={activeClassesByCourse}
        />
      )}
    </SmartCard>
  );
};

StudentFormHandler.propTypes = {
  t: PropTypes.func.isRequired,
  match: ReactRouterPropTypes.match.isRequired,
  history: ReactRouterPropTypes.history.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default withTranslation('common')(
  connect((state) => ({
    loading: state.loading,
  }))(StudentFormHandler)
);
